// UTM Tracking Utility for Firebase Analytics
import { logEvent, setUserProperties } from 'firebase/analytics';
import { analytics } from '../firebase/config';
import { trackCampaignLanding as trackFirestoreLanding, trackCampaignQuote as trackFirestoreQuote } from './firestoreMetrics';

// Campaign configurations
export const CAMPAIGNS = {
  'free-inspect-weed': {
    title: 'Free Inspection and Weed Pulling',
    source: 'door-to-door',
    medium: 'hangtag',
    campaign: 'free-inspect-weed',
    term: 'october-2025'
  },
  'flyer-1': {
    title: 'Junk Removal, Tree Trimming, Power Washing & Gutter Cleaning Special',
    source: 'flyer',
    medium: 'specific-date-junk-tree-trimming-power-washing-gutter-cleaning',
    campaign: 'flyer-1',
    term: 'october'
  },
  '25-off-6-months': {
    title: '25% Off 6 month plan',
    source: 'door-to-door',
    medium: 'hangtag',
    campaign: '25-off-6-months',
    term: 'october-2025'
  },
  '30-off-3-months': {
    title: '30% Off 3 month plan',
    source: 'door-to-door',
    medium: 'hangtag',
    campaign: '30-off-3-months',
    term: 'october-2025'
  }
};

// Extract UTM parameters from URL
export const extractUTMParameters = () => {
  const urlParams = new URLSearchParams(window.location.search);
  return {
    utm_source: urlParams.get('utm_source'),
    utm_medium: urlParams.get('utm_medium'),
    utm_campaign: urlParams.get('utm_campaign'),
    utm_term: urlParams.get('utm_term'),
    utm_content: urlParams.get('utm_content')
  };
};

// Track UTM campaign landing
export const trackCampaignLanding = async () => {
  try {
    const utmParams = extractUTMParameters();
    
    // Only track if UTM parameters are present
    if (utmParams.utm_campaign) {
      // Check if we've already tracked this campaign in this session
      const sessionKey = `campaign_tracked_${utmParams.utm_campaign}`;
      if (sessionStorage.getItem(sessionKey)) {
        console.log('Campaign already tracked in this session:', utmParams.utm_campaign);
        return;
      }

      // Track in Firestore (this will create the campaign if it doesn't exist)
      await trackFirestoreLanding(utmParams.utm_campaign);

      // Mark as tracked in this session
      sessionStorage.setItem(sessionKey, 'true');

      // Set user properties for the session (if analytics is available)
      if (analytics) {
        setUserProperties(analytics, {
          utm_source: utmParams.utm_source,
          utm_medium: utmParams.utm_medium,
          utm_campaign: utmParams.utm_campaign,
          utm_term: utmParams.utm_term,
          utm_content: utmParams.utm_content
        });

        // Log campaign landing event
        logEvent(analytics, 'campaign_landing', {
          campaign_name: utmParams.utm_campaign,
          campaign_source: utmParams.utm_source,
          campaign_medium: utmParams.utm_medium,
          campaign_term: utmParams.utm_term,
          campaign_content: utmParams.utm_content,
          landing_page: window.location.pathname,
          timestamp: new Date().toISOString()
        });
      }

      // Store in localStorage for session persistence
      localStorage.setItem('utm_campaign', JSON.stringify(utmParams));
      
      console.log('UTM Campaign tracked:', utmParams);
    }
  } catch (error) {
    console.error('Error tracking UTM campaign:', error);
  }
};

// Track campaign-specific actions
export const trackCampaignAction = (action, additionalData = {}) => {
  try {
    if (!analytics) return;

    const storedUTM = localStorage.getItem('utm_campaign');
    const utmParams = storedUTM ? JSON.parse(storedUTM) : {};

    if (utmParams.utm_campaign) {
      logEvent(analytics, 'campaign_action', {
        campaign_name: utmParams.utm_campaign,
        campaign_source: utmParams.utm_source,
        campaign_medium: utmParams.utm_medium,
        action_type: action,
        ...additionalData,
        timestamp: new Date().toISOString()
      });
    }
  } catch (error) {
    console.error('Error tracking campaign action:', error);
  }
};

// Track quote submission from campaign
export const trackCampaignQuote = async (quoteData) => {
  try {
    const storedUTM = localStorage.getItem('utm_campaign');
    const utmParams = storedUTM ? JSON.parse(storedUTM) : {};

    if (utmParams.utm_campaign) {
      // Track in Firestore
      await trackFirestoreQuote(utmParams.utm_campaign, quoteData);

      // Log in Firebase Analytics (if available)
      if (analytics) {
        logEvent(analytics, 'campaign_quote_submission', {
          campaign_name: utmParams.utm_campaign,
          campaign_source: utmParams.utm_source,
          campaign_medium: utmParams.utm_medium,
          campaign_term: utmParams.utm_term,
          quote_id: quoteData.id || 'unknown',
          services: quoteData.services || [],
          address: quoteData.address || 'unknown',
          estimated_value: quoteData.estimatedValue || 0,
          timestamp: new Date().toISOString()
        });
      }
    }
  } catch (error) {
    console.error('Error tracking campaign quote:', error);
  }
};

// Get current campaign info
export const getCurrentCampaign = () => {
  const storedUTM = localStorage.getItem('utm_campaign');
  if (storedUTM) {
    const utmParams = JSON.parse(storedUTM);
    return CAMPAIGNS[utmParams.utm_campaign] || null;
  }
  return null;
};

// Check if user came from a campaign
export const isFromCampaign = () => {
  return !!localStorage.getItem('utm_campaign');
};

// Clear campaign data (for testing)
export const clearCampaignData = () => {
  localStorage.removeItem('utm_campaign');
};
